/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   message success handler
 */

import {
  AFFORDABILITY_CALCULATOR_MESSAGE,
  DETAIL_MESSAGE,
  DETAIL_MESSAGE_SIMILAR,
  LIST_ITEM_MESSAGE,
  PERMANENT_ADDRESS_MESSAGE,
  UNKNOWN_MESSAGE,
} from '@zcode-ui/web-zumper-integrations'
import { PAGE_NAMES } from '@zcode/mixpanel'
import { AnalyticsC, Origin, trackGAEvent } from '@zcode/nr-utils'
import type { SendMessageSuccessResponse } from '@zcode/nr/es/shared/api'
import { getFilledFields } from '@zcode/nr/es/shared/utils/message'
import type { Listable, Property } from '@zcode/state-type'
import type { Dispatch } from 'redux'
import { logger } from '../../logger'
import { trackDetailEvent } from '../../modules/traktor/thunks/trackDetailEvent'
import { trackDetailSimilarEvent } from '../../modules/traktor/thunks/trackDetailSimilarEvent'
import { trackListItemEvent } from '../../modules/traktor/thunks/trackListItemEvent'

interface SendMessageSuccessProps {
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
  ctaName: string | undefined
  currentTab?: string
  email?: string | null
  entity: Listable | Property | null | undefined
  isFilterAd: boolean | null | undefined
  messagedListingIds?: readonly number[]
  messageModified?: boolean
  moveIn?: string | null
  name?: string
  oneTap?: boolean
  origin: Origin
  phone?: string | null
  result: SendMessageSuccessResponse
  saveSearch?: boolean
}

export const sendMessageSuccess =
  ({
    analyticChannel,
    currentTab,
    email,
    entity,
    isFilterAd,
    ctaName,
    messagedListingIds,
    messageModified,
    moveIn,
    name,
    origin,
    phone,
    result,
    saveSearch,
    oneTap = false,
  }: SendMessageSuccessProps) =>
  (dispatch: Dispatch) => {
    const filledFields = getFilledFields({ email, moveIn, name, phone })
    const filledFieldsText = filledFields.join(',')
    trackGAEvent(AnalyticsC.MESSAGE_FIELDS, AnalyticsC.STANDARD, filledFieldsText)

    const messageEventProps = {
      completedFields: filledFields,
      currentTab,
      createdAlert: saveSearch,
      ctaName,
      hasCustomizedMessage: messageModified,
      oneTap,
      isFilterAd,
      messagedListingIds,
      origin,
      messageId: result?.message_id,
    }
    const isDetail = analyticChannel === AnalyticsC.FULL_DETAIL
    const isList = analyticChannel === AnalyticsC.LIST || analyticChannel === AnalyticsC.LIST_ITEM
    const isSimilar = origin === Origin.SIMILAR
    const isPermanentAddress = analyticChannel === AnalyticsC.PERMANENT_ADDRESS
    const isProgrammaticRent = analyticChannel === AnalyticsC.PROGRAMMATIC_RENT
    if (isDetail && isSimilar) {
      dispatch(
        trackDetailSimilarEvent({
          name: DETAIL_MESSAGE_SIMILAR,
          similarEntity: entity,
          additionalProps: messageEventProps,
        })
      )
    } else if (isDetail) {
      dispatch(
        trackDetailEvent({
          name: DETAIL_MESSAGE,
          entity,
          additionalProps: messageEventProps,
        })
      )
    } else if (isList || isProgrammaticRent) {
      dispatch(
        trackListItemEvent({
          name: LIST_ITEM_MESSAGE,
          entity,
          additionalProps: messageEventProps,
        })
      )
    } else if (isPermanentAddress) {
      dispatch(
        trackListItemEvent({
          name: PERMANENT_ADDRESS_MESSAGE,
          entity,
          additionalProps: messageEventProps,
        })
      )
    } else if (analyticChannel === AnalyticsC.AFFORDABILITY_CALCULATOR) {
      dispatch(
        trackListItemEvent({
          name: AFFORDABILITY_CALCULATOR_MESSAGE,
          entity,
          additionalProps: messageEventProps,
        })
      )
    } else if (analyticChannel === AnalyticsC.HOMEPAGE) {
      dispatch(
        trackListItemEvent({
          name: LIST_ITEM_MESSAGE,
          entity,
          additionalProps: { ...messageEventProps, pageType: PAGE_NAMES.HOMEPAGE },
        })
      )
    } else {
      dispatch(
        trackListItemEvent({
          name: UNKNOWN_MESSAGE,
          entity,
          additionalProps: messageEventProps,
        })
      )
      logger.error(
        new Error(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `Traktor message event with unexpected analytic channel. analyticChannel: ${analyticChannel}, origin: ${origin}`
        )
      )
    }
  }
